import { useEffect, useState } from "react";
import { Search, Loader2, ScanLine, Sparkles } from "lucide-react";
import { FotoTarga } from "./FotoTarga";
import { fotoApi, fotoError } from "./fotoApi";
import { loadStampPrefs, saveStampPrefs, DEFAULT_STAMP } from "./fotoStamp";

const StampToggle = () => {
  const [prefs, setPrefs] = useState(DEFAULT_STAMP);
  useEffect(() => { loadStampPrefs().then(setPrefs).catch(() => {}); }, []);
  const toggle = () => {
    const next = { ...prefs, foto_on: !prefs.foto_on };
    setPrefs(next);
    saveStampPrefs(next).catch(() => setPrefs(prefs));
  };
  return (
    <button type="button" role="switch" aria-checked={prefs.foto_on} onClick={toggle} data-testid="foto-stamp-toggle"
      className="w-full flex items-center justify-between rounded-xl border border-slate-300 bg-white px-4 py-3 text-left">
      <span>
        <span className="block text-base font-semibold text-slate-900">Timbra data/ora</span>
        <span className="block text-xs text-slate-500">Scrive data e ora di scatto su ogni foto</span>
      </span>
      <span className={`relative h-7 w-12 rounded-full transition-colors ${prefs.foto_on ? "bg-amber-500" : "bg-slate-300"}`}>
        <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${prefs.foto_on ? "translate-x-5" : "translate-x-0.5"}`} />
      </span>
    </button>
  );
};

export const FotoSearch = ({ onSelect, aiTarga }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [scan, setScan] = useState(null);

  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    const t = setTimeout(() => {
      setBusy(true); setErr("");
      fotoApi.get("/pratiche", { params: { q } }).then(({ data }) => setRes(data)).catch((e) => setErr(fotoError(e))).finally(() => setBusy(false));
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  if (scan) return <FotoTarga mode={scan} aiEnabled={aiTarga} onSelect={onSelect} onClose={() => setScan(null)} />;

  return (
    <div className="space-y-3" data-testid="foto-search">
      <StampToggle />
      <div className={`grid gap-2 ${aiTarga ? "grid-cols-2" : "grid-cols-1"}`}>
        <button type="button" onClick={() => setScan("device")} data-testid="foto-cerca-targa"
          className="h-14 rounded-xl bg-slate-900 text-white text-base font-semibold flex items-center justify-center gap-2 active:bg-slate-800 transition-colors">
          <ScanLine className="h-6 w-6 text-amber-400 shrink-0" /> Cerca targa
        </button>
        {aiTarga && (
          <button type="button" onClick={() => setScan("ai")} data-testid="foto-cerca-targa-ai"
            className="h-14 rounded-xl bg-amber-500 text-slate-900 text-base font-semibold flex items-center justify-center gap-2 px-2 leading-tight active:bg-amber-600 transition-colors">
            <Sparkles className="h-5 w-5 shrink-0" /> Cerca targa con AI
          </button>
        )}
      </div>
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome cliente o targa" autoFocus enterKeyHint="search" data-testid="foto-search-input"
          className="w-full h-14 rounded-xl border border-slate-300 bg-white pl-12 pr-4 text-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500" />
        {busy && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 animate-spin text-amber-500" />}
      </div>
      {err && <p className="text-sm text-red-700" role="alert" data-testid="foto-search-error">{err}</p>}
      {!busy && q.trim().length >= 2 && !res.length && !err && <p className="text-sm text-slate-500" data-testid="foto-search-empty">Nessuna pratica trovata.</p>}
      <ul className="space-y-2" data-testid="foto-search-results">
        {res.map((p) => (
          <li key={p.id}>
            <button type="button" onClick={() => onSelect(p)} data-testid={`foto-result-${p.id}`}
              className="w-full text-left rounded-xl border border-slate-200 bg-white px-4 py-3 active:bg-amber-50 transition-colors">
              <div className="text-base font-semibold text-slate-900">{p.cliente || "Cliente non indicato"}</div>
              <div className="text-sm text-slate-500 font-mono">{p.numero_pratica}{p.targa ? ` · ${p.targa}` : ""}</div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
